import { useCallback, useEffect, useId, useState } from 'react';

import type { Breadcrumb, DonationForm, IPredicate } from '@feathr/blackbox';
import type { ISorted } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import type { IDonationCreatedEvent } from '../../hooks';
import { breadcrumbToTransactionEvent, DONATION_LIST_PARAMS } from './TransactionTable.utils';

/**
 * Maps table column IDs to API field paths for server-side sorting.
 * Only includes fields that are sortable server-side (direct Breadcrumb fields).
 * Fields requiring joins (e.g., person name) are not sortable server-side.
 */
const SORT_FIELD_MAP: Record<string, string> = {
  time: 'd_c',
  amount: 'donation.amount',
  credits_earned: 'growth_credits',
  donation_type: 'donation.frequency',
  // form_name is not sortable server-side (requires join to DonationForms)
};

/** Default sort: most recent donations first */
const DEFAULT_SORT: ISorted[] = [{ id: 'time', desc: true }];

interface IUseHistoricalDonationsReturn {
  /** Transformed donation events from historical breadcrumbs */
  donations: IDonationCreatedEvent[];
  /** Whether historical data is loading */
  isLoading: boolean;
  /** Whether there was an error loading data */
  isErrored: boolean;
  /** Current page (1-based, for display and API calls) */
  page: number;
  /** Current page index (0-based, for Table component) */
  tablePageIndex: number;
  /** Total number of pages */
  totalPages: number;
  /** Number of items per page */
  pageSize: number;
  /** Current sort configuration */
  sort: ISorted[];
  /** Go to next page */
  nextPage: () => void;
  /** Go to previous page */
  prevPage: () => void;
  /** Go to specific page */
  goToPage: (page: number) => void;
  /** Change sort order (triggers server-side re-fetch) */
  onSortChange: (newSort: ISorted[]) => void;
  /** Refresh the data */
  refresh: () => void;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * Hook for fetching and transforming historical donation breadcrumbs.
 *
 * IMPORTANT: The component using this hook MUST be wrapped with observer()
 * from mobx-react-lite for proper reactivity.
 *
 * @param pageSize - Number of items per page (default: 10)
 * @param additionalPredicates - Additional predicates to merge with the base donation predicate
 * @returns Historical donations data with pagination controls
 *
 * @example
 * ```tsx
 * const TransactionTable = observer(function TransactionTable() {
 *   const { donations, isLoading, page, totalPages, nextPage, prevPage } =
 *     useHistoricalDonations(10);
 *
 *   if (isLoading) return <Spinner />;
 *   return <Table items={donations} />;
 * });
 * ```
 */
function useHistoricalDonations(
  pageSize = DEFAULT_PAGE_SIZE,
  additionalPredicates: IPredicate[] = [],
): IUseHistoricalDonationsReturn {
  const { Breadcrumbs, Persons, DonationForms } = useStore();
  const baseListId = useId();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<ISorted[]>(DEFAULT_SORT);
  const [breadcrumbsResponse, setBreadcrumbsResponse] = useState<ListResponse<Breadcrumb> | null>(
    null,
  );
  const [donationFormsResponse, setDonationFormsResponse] =
    useState<ListResponse<DonationForm> | null>(null);

  // Fetch donation forms for form names
  useEffect(() => {
    const formsResponse = DonationForms.list({ pagination: { page_size: 1000 } });
    setDonationFormsResponse(formsResponse);
  }, [DonationForms]);

  // Convert sort state to API ordering parameter
  const ordering = sort
    .filter((s) => SORT_FIELD_MAP[s.id])
    .map((s) => `${s.desc ? '-' : ''}${SORT_FIELD_MAP[s.id]}`);

  // Create a stable key for predicates to detect changes
  const predicatesKey = JSON.stringify(additionalPredicates);

  // Include ordering, page, and predicates in listId so each configuration gets its own cache entry
  const orderingKey = ordering.length > 0 ? ordering.join(',') : '-d_c';
  const listId = `${baseListId}-${orderingKey}-page${page}-pred${predicatesKey}`;

  // Reset to page 1 when predicates change
  useEffect(() => {
    setPage(1);
  }, [predicatesKey]);

  // Fetch breadcrumbs with pagination and sorting
  useEffect(() => {
    const mergedPredicates = [...DONATION_LIST_PARAMS.predicates!, ...additionalPredicates];

    const response = Breadcrumbs.list(
      {
        ...DONATION_LIST_PARAMS,
        predicates: mergedPredicates,
        pagination: {
          page,
          page_size: pageSize,
        },
        ordering: ordering.length > 0 ? ordering : ['-d_c'],
      },
      {},
      listId,
    );
    setBreadcrumbsResponse(response);
  }, [Breadcrumbs, listId, page, pageSize, predicatesKey]);

  // Access MobX observable properties directly (not in useMemo)
  // This is required for observer() to detect changes and trigger re-renders
  const hasLoadedOnce = breadcrumbsResponse?.hasLoadedOnce ?? false;
  const isPending = breadcrumbsResponse?.isPending ?? true;
  const isErrored = breadcrumbsResponse?.isErrored ?? false;
  const models = breadcrumbsResponse?.models ?? [];
  const paginationCount = breadcrumbsResponse?.pagination?.count ?? 0;

  const formsLoaded = donationFormsResponse?.hasLoadedOnce ?? false;
  const formModels = donationFormsResponse?.models ?? [];

  // Transform breadcrumbs to donation events
  // NOTE: We intentionally don't use useMemo here because we need MobX observer
  // to detect changes to the models array. The transformation is fast enough
  // that re-computing on every render is acceptable.
  let donations: IDonationCreatedEvent[] = [];
  if (hasLoadedOnce && !isPending) {
    donations = models.map((breadcrumb) => {
      const perId = breadcrumb.get('per_id');
      const formId = breadcrumb.get('form_id');

      // Get person name
      let personData = { name: 'Anonymous', isPending: false, isErrored: false };
      if (perId) {
        const person = Persons.get(perId);
        personData = {
          name: person.name,
          isPending: person.isPending,
          isErrored: person.isErrored,
        };
      }

      // Get form name
      let formName = 'Unknown Form';
      if (formId && formsLoaded) {
        const form = formModels.find((f) => f.get('id') === formId);
        formName = form?.get('name') || 'Unknown Form';
      }

      return breadcrumbToTransactionEvent(breadcrumb, personData, formName);
    });
  }

  // Calculate pagination info
  const totalPages = paginationCount > 0 ? Math.ceil(paginationCount / pageSize) : 1;

  const isLoading = !hasLoadedOnce || isPending;

  const nextPage = useCallback(() => {
    setPage((prev) => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  const prevPage = useCallback(() => {
    setPage((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToPage = useCallback(
    (newPage: number) => {
      setPage(Math.max(1, Math.min(newPage, totalPages)));
    },
    [totalPages],
  );

  const refresh = useCallback(() => {
    Breadcrumbs.refreshApiCache();
  }, [Breadcrumbs]);

  const onSortChange = useCallback((newSort: ISorted[]) => {
    setSort(newSort);
    // Reset to page 1 when sort changes
    setPage(1);
  }, []);

  return {
    donations,
    isLoading,
    isErrored,
    page,
    tablePageIndex: page - 1,
    totalPages,
    pageSize,
    sort,
    nextPage,
    prevPage,
    goToPage,
    onSortChange,
    refresh,
  };
}

export default useHistoricalDonations;
export type { IUseHistoricalDonationsReturn };
